/**
 * SportsEvent JSON-LD for public tournament shells.
 * CommonJS so the request-time shell and the React prerender share one shape.
 */

const { CANONICAL_ORIGIN } = require("./tournamentSubpages");

const eventPath = (tournament) => {
  const key = tournament?.slug || tournament?.id || "";
  if (!key) return "/tournaments";
  return `/tournament/${key}`;
};

const priceText = (amount) => {
  if (amount == null || amount === "") return "";
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "";
  return value.toFixed(2);
};

const entryFeesFromEvents = (events) => {
  const priced = [];
  for (const event of events || []) {
    if (!event || event.isDeleted || event.templateId) continue;
    const rows = event.eventFee || event.fees || [];
    const total = rows.reduce((sum, row) => {
      const amount = Number(row?.amount);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
    const price = priceText(total);
    if (!price) continue;
    priced.push({
      name: event.eventName || "Entry",
      price,
      isPrimary: Boolean(event.isPrimary),
    });
  }
  const primary = priced.filter((row) => row.isPrimary);
  const chosen = primary.length ? primary : priced;
  return chosen.map(({ name, price }) => ({ name, price }));
};

const placeFromLocation = (location) => {
  if (!location) return null;
  const center = location.bowlingCenters || location.center || {};
  const name = String(center.centerName || location.name || "").trim();
  const city = String(location.city || center.city || "").trim();
  const region = String(
    location.state || center.state || center.stateShort || ""
  ).trim();
  const street = String(
    center.formattedAddress ||
      center.streetFirstLine ||
      location.streetAddress ||
      ""
  ).trim();
  const postal = String(location.zipCode || center.zipCode || "").trim();
  if (!name && !city) return null;

  const place = {
    "@type": "Place",
    name: name || city,
  };
  if (street || city || region || postal) {
    place.address = {
      "@type": "PostalAddress",
      addressCountry: "US",
      ...(street ? { streetAddress: street } : {}),
      ...(city ? { addressLocality: city } : {}),
      ...(region ? { addressRegion: region } : {}),
      ...(postal ? { postalCode: postal } : {}),
    };
  }
  return place;
};

const offerFromFee = (fee, url) => ({
  "@type": "Offer",
  name: fee.name,
  price: fee.price,
  priceCurrency: "USD",
  availability: "https://schema.org/InStock",
  url,
});

const offersFor = (tournament, url) => {
  const listed = Array.isArray(tournament.entryFees) ? tournament.entryFees : [];
  let fees = listed
    .map((fee) => ({
      name: fee?.name || tournament.name || "Entry",
      price: priceText(fee?.price),
    }))
    .filter((fee) => fee.price);
  if (!fees.length) {
    const price = priceText(tournament.entryFee);
    fees = price ? [{ name: tournament.name || "Entry", price }] : [];
  }
  if (!fees.length) return undefined;
  const offers = fees.map((fee) => offerFromFee(fee, url));
  return offers.length === 1 ? offers[0] : offers;
};

const organizerFor = (tournament) => {
  const org = tournament.organizations || tournament.organization;
  const name = String(org?.name || "").trim();
  if (!name) return undefined;
  const url = org.url || org.website;
  return {
    "@type": "Organization",
    name,
    ...(url ? { url } : {}),
  };
};

const tournamentEventJsonLd = (tournament) => {
  if (!tournament?.name) return null;

  const url = `${CANONICAL_ORIGIN}${eventPath(tournament)}`;
  const places = (tournament.locations || [])
    .map(placeFromLocation)
    .filter(Boolean);
  const organizer = organizerFor(tournament);
  const offers = offersFor(tournament, url);
  const startDate = tournament.startDate || undefined;
  const endDate = tournament.endDate || startDate;

  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: tournament.name,
    description:
      tournament.description || `Bowling tournament: ${tournament.name}`,
    url,
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    ...(places.length === 1 ? { location: places[0] } : {}),
    ...(places.length > 1 ? { location: places } : {}),
    ...(organizer ? { organizer } : {}),
    ...(offers ? { offers } : {}),
    sport: "Bowling",
    ...(tournament.maxParticipants
      ? { maximumAttendeeCapacity: tournament.maxParticipants }
      : {}),
  };
};

const jsonLdScript = (data) => {
  if (!data) return "";
  const json = JSON.stringify(data).replace(/</g, "\\u003c");
  return `<script type="application/ld+json">${json}</script>`;
};

module.exports = {
  entryFeesFromEvents,
  jsonLdScript,
  placeFromLocation,
  tournamentEventJsonLd,
};
